// Import the functions you need from the SDKs you need
import { initializeApp, getApps } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import { getAuth } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";

/**
 * Firebase Configuration
 *
 * Uses REACT_APP_FIREBASE_* from .env (e.g. copy from .env.dealer).
 * REACT_APP_ACTIVE_COMPANY sets Firestore companyId (e.g. dealer).
 */

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || undefined
};

// Firestore company isolation (companyId)
export const currentCompanyName = process.env.REACT_APP_ACTIVE_COMPANY || 'dealer';

if (process.env.NODE_ENV === 'development' && typeof console !== 'undefined') {
  console.info('[Firebase] projectId:', firebaseConfig.projectId, '| companyId:', currentCompanyName);
}

// Validate that all required Firebase config values are present
if (!firebaseConfig.apiKey || !firebaseConfig.authDomain || !firebaseConfig.projectId) {
  const errorMsg =
    'Missing required Firebase configuration.\n\n' +
    'Copy .env.dealer (or another .env.<company>) to .env and set REACT_APP_FIREBASE_* and REACT_APP_ACTIVE_COMPANY.\n\n' +
    'Required variables:\n' +
    '- REACT_APP_FIREBASE_API_KEY\n' +
    '- REACT_APP_FIREBASE_AUTH_DOMAIN\n' +
    '- REACT_APP_FIREBASE_PROJECT_ID\n' +
    '- REACT_APP_FIREBASE_MESSAGING_SENDER_ID\n' +
    '- REACT_APP_FIREBASE_APP_ID';

  // In development, show a helpful error instead of crashing
  if (process.env.NODE_ENV === 'development') {
    console.error('❌', errorMsg);
    // Create a visible error element in the DOM
    if (typeof document !== 'undefined') {
      const errorDiv = document.createElement('div');
      errorDiv.style.cssText = 'position: fixed; top: 0; left: 0; right: 0; background: #ff4444; color: white; padding: 20px; z-index: 10000; font-family: monospace; white-space: pre-wrap;';
      errorDiv.textContent = errorMsg;
      document.body.appendChild(errorDiv);
    }
  }

  throw new Error(errorMsg);
}

// Initialize Firebase (only if not already initialized)
let app;
const existingApps = getApps();
if (existingApps.length > 0) {
  app = existingApps[0];
} else {
  app = initializeApp(firebaseConfig);
}

// Initialize Firebase services
// Analytics initialization with error handling (may fail if API not enabled)
let analytics = null;
if (typeof window !== 'undefined') {
  try {
    analytics = getAnalytics(app);
  } catch (error) {
    // Analytics may fail if Firebase Installations API is not enabled
    // This is not critical for app functionality
    console.warn('Firebase Analytics initialization failed (this is not critical):', error.message);
    analytics = null;
  }
}
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);

export { app, analytics, auth, db, storage, firebaseConfig };
